/**
 * ChatMessageRenderer — renders a single chat message (user, assistant, or
 * tool_result). Assistant messages render as Markdown with GFM tables plus a
 * muted meta row (duration / tokens / model / copy). Memoized so the post-
 * stream reconcile never repaints untouched bubbles (finish-flash fix, B1).
 */
import { memo, useState } from 'react';
import { Box, Typography, Dialog, IconButton, Chip, Tooltip } from '@mui/material';
import { Close, ZoomIn, ContentCopy, Check } from '@mui/icons-material';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import { alpha, useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../services/chatService';
import { ToolCallMessage, ToolResultMessage } from './ChatToolCards';
import { formatDuration, formatTokens, resolveApiHref, isApiHref, openAuthenticatedApiFile } from './chatMessageUtils';

interface Props {
  message: ChatMessage;
  isStreaming?: boolean;
  /** Short model label for the assistant meta row (e.g. "Sonnet 5"). */
  modelShort?: string;
}

/** Entrance animation — transform/opacity only, disabled under reduced motion. */
const entranceSx = {
  '@keyframes chat-msg-in': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'chat-msg-in 0.25s ease-out',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

const ChatMessageRendererImpl = ({ message, isStreaming, modelShort }: Props) => {
  if (message.Role === 'tool_result') return <ToolResultMessage message={message} />;
  if (message.Role === 'assistant' && message.ToolName) return <ToolCallMessage message={message} />;

  const isUser = message.Role === 'user';
  return isUser
    ? <UserBubble message={message} />
    : <AssistantBubble message={message} isStreaming={isStreaming} modelShort={modelShort} />;
};

/**
 * Memoized: the reconcile keeps object references stable for unchanged rows,
 * so a post-stream state update re-renders ONLY the rows that changed.
 */
export const ChatMessageRenderer = memo(ChatMessageRendererImpl);

function UserBubble({ message }: { message: ChatMessage }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, px: 2, ...entranceSx }}>
      <Box sx={{
        maxWidth: 'min(72ch, 70%)', px: 2, py: 1, borderRadius: '16px 16px 4px 16px',
        backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText,
      }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6 }}>
          {message.Content}
        </Typography>
      </Box>
    </Box>
  );
}

/** Custom image renderer — shows thumbnails with click-to-expand lightbox */
function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  if (!src) return null;
  return (
    <>
      <Box sx={{
        display: 'inline-block', position: 'relative', my: 1, cursor: 'pointer',
        borderRadius: 1, overflow: 'hidden', border: `1px solid ${theme.palette.divider}`,
        maxWidth: 280, transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 3 },
        '&:hover .zoom-icon': { opacity: 1 },
      }} onClick={() => setOpen(true)}>
        <img src={src} alt={alt || 'Image'} style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 200, objectFit: 'cover' }} />
        <Box className="zoom-icon" sx={{
          position: 'absolute', bottom: 6, right: 6, backgroundColor: alpha(theme.palette.common.black, 0.5),
          borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: 0, transition: 'opacity 0.2s',
        }}>
          <ZoomIn sx={{ fontSize: 16, color: theme.palette.common.white }} />
        </Box>
        {alt && <Typography variant="caption" sx={{ display: 'block', px: 1, py: 0.5, fontSize: '0.68rem', color: 'text.secondary', backgroundColor: theme.palette.grey[50] }}>{alt}</Typography>}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', backgroundColor: theme.palette.common.black } }}>
        <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: theme.palette.common.white, zIndex: 1, backgroundColor: alpha(theme.palette.common.black, 0.4) }}>
          <Close />
        </IconButton>
        <img src={src} alt={alt || 'Image'} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }} />
      </Dialog>
    </>
  );
}

/** Custom link renderer — rewrites /api/ paths to backend, opens all in new tab */
function MarkdownLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const theme = useTheme();
  // API-served links (CSV exports, report PDFs) need the Bearer token — a raw
  // new-tab navigation fails with "Missing Authorization header". Fetch them
  // through the authenticated client instead.
  if (isApiHref(href)) {
    return (
      <a href={resolveApiHref(href)} rel="noopener noreferrer"
        onClick={(e) => { e.preventDefault(); void openAuthenticatedApiFile(href!); }}
        style={{ color: theme.palette.primary.main, fontWeight: 500, cursor: 'pointer' }}>
        {children}
      </a>
    );
  }
  return (
    <a href={resolveApiHref(href)} target="_blank" rel="noopener noreferrer"
      style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
      {children}
    </a>
  );
}

function AssistantBubble({ message, isStreaming, modelShort }: { message: ChatMessage; isStreaming?: boolean; modelShort?: string }) {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!message.Content) return;
    navigator.clipboard.writeText(message.Content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const duration = formatDuration(message.DurationMs);
  const tokensIn = formatTokens(message.InputTokens);
  const tokensOut = formatTokens(message.OutputTokens);
  const showMeta = !isStreaming && Boolean(duration || tokensIn || tokensOut || message.Content);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5, px: 2, ...entranceSx }}>
      <Box sx={{ display: 'flex', gap: 1.5, maxWidth: '85%', alignItems: 'flex-start', minWidth: 0 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '50%', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5,
          backgroundColor: alpha(theme.chat.botAccent, 0.1),
        }}>
          <MoftakiBotIcon size={20} />
        </Box>
        <Box sx={{ minWidth: 0, '&:hover .meta-copy': { opacity: 1 } }}>
          {/* Surface card bubble */}
          <Box sx={{
            px: 1.75, py: 1.25, borderRadius: '4px 16px 16px 16px', maxWidth: '72ch',
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            boxShadow: 1,
            '& p': { m: 0, fontSize: '0.85rem', lineHeight: 1.7, color: 'text.primary' },
            '& p + p': { mt: 1 },
            '& strong': { fontWeight: 600 },
            '& pre': {
              backgroundColor: theme.palette.grey[100], border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1, p: 1.5, overflow: 'auto', fontSize: '0.78rem', my: 1,
            },
            '& code': { fontSize: '0.8rem', fontFamily: "'Fira Code', monospace" },
            '& :not(pre) > code': {
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              px: 0.5, py: 0.25, borderRadius: 0.5,
            },
            // Table styles — proper GFM pipe table rendering
            '& table': {
              borderCollapse: 'collapse', width: '100%', my: 1.5, fontSize: '0.8rem',
              border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden',
            },
            '& thead': { backgroundColor: alpha(theme.palette.primary.main, 0.07) },
            '& th': {
              border: `1px solid ${theme.palette.divider}`, px: 1.5, py: 0.75,
              textAlign: 'left', fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap',
            },
            '& td': {
              border: `1px solid ${theme.palette.divider}`, px: 1.5, py: 0.6,
              textAlign: 'left', fontSize: '0.78rem',
            },
            '& tbody tr:nth-of-type(even)': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
            '& tbody tr:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
            '& ul, & ol': { pl: 2.5, fontSize: '0.85rem', my: 0.5 },
            '& li': { mb: 0.25 },
            '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, my: 1.5 },
            '& blockquote': {
              borderLeft: `3px solid ${theme.palette.primary.main}`, pl: 1.5, ml: 0, my: 1,
              color: 'text.secondary', fontStyle: 'italic',
            },
            overflowX: 'auto',
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink, img: MarkdownImage }}>{message.Content || ''}</ReactMarkdown>
            {isStreaming && (
              <Box component="span" sx={{
                display: 'inline-block', width: 6, height: 16, ml: '2px',
                backgroundColor: theme.chat.botAccent, borderRadius: '1px', verticalAlign: 'text-bottom',
                animation: 'cursor-blink 0.8s step-end infinite',
                '@keyframes cursor-blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }} />
            )}
          </Box>

          {/* Meta row — duration · tokens · model · copy (copy appears on hover) */}
          {showMeta && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4, ml: 0.5, minHeight: 20 }}>
              {duration && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', opacity: 0.75 }}>
                  {duration}
                </Typography>
              )}
              {(tokensIn || tokensOut) && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', opacity: 0.75 }}>
                  {tokensIn || '0'} in / {tokensOut || '0'} out
                </Typography>
              )}
              {modelShort && (
                <Chip label={modelShort} size="small" variant="outlined"
                  sx={{ height: 16, fontSize: '0.58rem', color: 'text.secondary', opacity: 0.85, '& .MuiChip-label': { px: 0.75 } }} />
              )}
              {message.Content && (
                <Tooltip title={copied ? 'Copied' : 'Copy as markdown'}>
                  <IconButton className="meta-copy" size="small" onClick={handleCopy}
                    sx={{ p: 0.25, opacity: 0, transition: 'opacity 0.15s', '@media (hover: none)': { opacity: 0.6 } }}>
                    {copied
                      ? <Check sx={{ fontSize: 13, color: theme.palette.primary.main }} />
                      : <ContentCopy sx={{ fontSize: 13, color: 'text.secondary' }} />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
